/*
  RightClickMenu.jsx
*/

// Npm.
import React from 'react';

// Lib.
import getUniqueNodeId from '../../utils/getUniqueNodeId';
import getUniqueLinkId from '../../utils/getUniqueLinkId';
import deleteNodeFromGraph from '../../utils/deleteNodeFromGraph';

// Components.
import Popper from '../Popper';
import List from '../List';

// Hooks.
import useReadOnly from '../../hooks/useReadOnly';

/**
 * Helper to apply collapsed or expanded state to node.
 */
function setCollapsedOnNode(node, collapsed) {
  const next = {
    ...node,
    _botech: { ...node._botech }
  };
  if (collapsed) {
    next._botech.collapsed = true;
  } else {
    delete next._botech.collapsed;
  }
  return next;
}

/**
 * Menu when something is right-clicked on the graph.
 */
export default function RightClickMenu({
  selectedNode,
  node,
  link,
  coords,
  //closestNode,
  //closestPair,
  xPos,
  yPos,
  graphData,
  onClose,
  onChangeGraphData,
  onChangeSelectedNode,
  onChangeSelectedLink,
  ...rest
}) {

  const readOnly = useReadOnly();

  // Handlers.

  function handleClickOption(event, value, option) {
    event.preventDefault();
    
    let next = graphData || {};

    const nodes = next.nodes || [];
    const links = next.links || [];

    const newNodes = [];
    const newLinks = [];
    const replaceNodes = new Map();

    function generateNode() {
      return {
        id: getUniqueNodeId(graphData),
        x: (coords?.x ?? node?.x) || 0,
        y: (coords?.y ?? node?.y) || 0,
      };
    }

    switch (value) {

      case 'source': {
        const newNode = generateNode();
        newNodes.push(newNode);
        if (node) {
          newLinks.push({ id: getUniqueLinkId(next), source: newNode, target: node });
        }
        break;
      }

      case 'target': {
        const newNode = generateNode();
        newNodes.push(newNode);
        if (node) {
          newLinks.push({ id: getUniqueLinkId(next), source: node, target: newNode });
        }
        break;
      }

      case 'create': {
        const newNode = generateNode();
        newNodes.push(newNode);
        //const source = selectedNode || closestNode;
        const source = selectedNode;
        if (source) {
          newLinks.push({ id: getUniqueLinkId(next), source, target: newNode });
        }
        break;
      }

      case 'collapse': {
        if (node) {
          replaceNodes.set(node, setCollapsedOnNode(node, true));
        }
        break;
      }

      case 'expand': {
        if (node) {
          replaceNodes.set(node, setCollapsedOnNode(node, false));
        }
        break;
      }

      case 'expand-all': {
        if (node) {
          replaceNodes.set(node, setCollapsedOnNode(node, false));
        }
        break;
      }

      // case 'link': {
      //   if (closestPair) {
      //     newLinks.push({ source: closestPair[1], target: closestPair[0] });
      //   }
      //   break;
      // }

      case 'link-selected': {
        if (node && selectedNode && selectedNode !== node) {
          newLinks.push({ id: getUniqueLinkId(next), source: selectedNode, target: node });
        }
        break;
      }

      case 'delete': {
        if (node) {
          next = deleteNodeFromGraph(next, node);
        }
        if (link) {
          next = {
            ...next,
            links: links.filter(obj => obj !== link),
          };
        }
        break;
      }

      default: {}

    }

    if (newNodes.length > 0 || newLinks.length > 0) {
      next = {
        ...next,
        nodes: newNodes.length > 0 ? nodes.concat(newNodes) : nodes,
        links: newLinks.length > 0 ? links.concat(newLinks) : links,
      };
    }

    if (replaceNodes.size > 0) {
      next = {
        ...next,
        nodes: nodes.map(obj => replaceNodes.get(obj) || obj),
        links: links.map(obj => replaceNodes.has(obj.source) || replaceNodes.has(obj.target) ? {
          ...obj,
          source: replaceNodes.get(obj.source) || obj.source,
          target: replaceNodes.get(obj.target) || obj.target,
        } : obj)
      };
    }

    if (onChangeGraphData && next !== graphData) {
      onChangeGraphData(next);
    }

    if (onChangeSelectedNode && newNodes.length > 0) {
      onChangeSelectedNode(newNodes[0]);
    } else if (onChangeSelectedLink && newLinks.length > 0) {
      onChangeSelectedLink(newLinks[0]);
    }

    if (onClose) {
      onClose();
    }

  }

  // Props.

  const options = [];

  if (node) {
    //options.push(
    //  { label: '+ Create Target', value: 'target' },
    //  { label: '+ Create Source', value: 'source' }
    //);
    if (!readOnly && selectedNode && selectedNode !== node) {
      options.push({ label: '→ Create Link', value: 'link-selected' });
    }
    if (node._botech?.collapsed) {
      options.push(
        { label: '+ Expand Node', value: 'expand' },
        //{ label: '+ Expand All', value: 'expand-all' },  // TODO.
      );
    } else {
      options.push({ label: '- Collapse Node', value: 'collapse' });
    }
    if (!readOnly) {
      options.push({ label: '× Delete Node', value: 'delete' });
    }
  } else if (link) {
    if (!readOnly) {
      options.push({ label: '× Delete Link', value: 'delete' });
    }
  } else if (!readOnly) {
    options.push({ label: '+ Create Node', value: 'create' });
    //if (graphData.nodes?.length) {
      //options.push({ label: '+ Create Link', value: 'link' });
    //}
  }

  return (
    <Popper open xPos={xPos} yPos={yPos} onClose={onClose}>
      <List
        dense
        options={options}
        onClickOption={handleClickOption}
      />
    </Popper>
  );
}
